import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { MetricDeltas } from '@/components/sales-report/components/MetricDeltas'
import { SegmentedToggle } from '@/components/sales-report/components/SegmentedToggle'
import type { VolumeMonthly } from '../types'
import { MONO, fmt, fmtUnits, monthLabel } from './format'
import { MonthBars } from './MonthBars'
import { childHint } from './CategorySplit'
import { PC_VIEWS, lineTerm, volumeAdd, volumeZero, type Group, type LineRef, type PcView, type VolumeFig } from './classification'

const CARD = 'min-w-0 rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900'
const CHAMP = '#AE9056'
const STONE = '#C9C2B6'
const SLATE = '#5E6E8C'

type Comparison = { current: number; prevMonth: number | null; prevYear: number | null; prevYearLabel: string; prevLabel: string }

/** Volume mix — units (paid vs included) by month, and the TOTAL PRODUCTION VALUE
 *  of everything produced however it was funded: new cash + PC credit + the retail
 *  value of included work. Every figure reflects the selected date range. */
export function VolumeMixCard({
  volume,
  currency,
  monthIdx,
  months,
  periodLabel,
  comparison,
  groups,
  view,
  onViewChange,
}: {
  volume: { monthly: VolumeMonthly; includedValue: number; includedValueMonthly: number[] }
  currency: string
  monthIdx: number
  months: string[]
  periodLabel: string
  comparison: Comparison
  /** Department groups of windowed volume (Planning Center / Lab / Unclassified);
   *  null on a v1 bundle, which carries no category split. */
  groups: Group<VolumeFig>[] | null
  view: PcView
  onViewChange: (v: PcView) => void
}) {
  const [hov, setHov] = useState<number | null>(null)
  const active = hov ?? monthIdx
  const m = volume.monthly
  const maxUnits = Math.max(1, ...m.totalUnits)
  // Total = the departments' subtotals (Planning Center + Lab + Unclassified).
  const tot = (groups ?? []).reduce((a, g) => volumeAdd(a, g.subtotal), volumeZero())
  const totUnits = tot.paidUnits + tot.includedUnits

  // Total production value = new cash + PC credit + retail value of included work.
  // On a v1 bundle (no category rows) the paid side falls back to the monthly series.
  const newCash = groups ? tot.paidCardInv : m.cashCollected.reduce((a, b) => a + b, 0)
  const pcCredit = groups ? tot.paidPcCredit : m.pcCredit.reduce((a, b) => a + b, 0)
  const includedRetail = volume.includedValue
  const production = newCash + pcCredit + includedRetail
  const pctOf = (v: number) => (production ? (v / production) * 100 : 0)

  // Included pricing isn't recorded by category, so distribute the range's
  // included retail value across the categories by each one's included units ×
  // its average paid price. The weights are taken at category level (disciplines,
  // Lab categories, Unclassified) so a department's share is the same in the
  // Discipline and Stage views; the Stage rows then split the Planning Center
  // share by their own weights.
  const avgPrice = (c: VolumeFig) => (c.paidUnits ? c.paidValue / c.paidUnits : tot.paidUnits ? tot.paidValue / tot.paidUnits : 0)
  const weightOf = (c: VolumeFig) => c.includedUnits * avgPrice(c)
  const deptWeight = (g: Group<VolumeFig>) => g.leaves.reduce((a, c) => a + weightOf(c), 0)
  const totalWeight = (groups ?? []).reduce((a, g) => a + deptWeight(g), 0)
  const deptIncl = (g: Group<VolumeFig>) => (totalWeight ? (includedRetail * deptWeight(g)) / totalWeight : 0)
  const childIncl = (g: Group<VolumeFig>, c: VolumeFig) => {
    const w = g.children.reduce((a, x) => a + weightOf(x.value), 0)
    return w ? (deptIncl(g) * weightOf(c)) / w : 0
  }

  return (
    <div className={CARD}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
          Volume mix
          <InfoPopover term="productionValue" />
        </h3>
        <span className="text-[11px] text-stone-400 dark:text-stone-500">production value &amp; units · paid vs included · {periodLabel}</span>
      </div>

      {/* Monthly stacked units: paid (bottom) + included (top) — hover for the month's units + value */}
      <div className="mt-4">
        <MonthBars months={months} onHover={setHov} isActive={(i) => i === active}>
          {(i) => {
            const on = i === active
            return (
              <div className="flex w-full flex-col-reverse overflow-hidden rounded-t" style={{ height: 72 }}>
                <div className="w-full" style={{ height: Math.round((m.paidUnits[i] / maxUnits) * 72), backgroundColor: CHAMP, opacity: on ? 1 : 0.7 }} />
                <div className="w-full" style={{ height: Math.round((m.includedUnits[i] / maxUnits) * 72), backgroundColor: STONE, opacity: on ? 1 : 0.7 }} />
              </div>
            )
          }}
        </MonthBars>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-stone-500 dark:text-stone-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CHAMP }} />
          Paid <span className="font-medium text-stone-700 dark:text-stone-200">{fmtUnits(m.paidUnits[active])}</span> · <span className={cn('text-stone-600 dark:text-stone-300', MONO)}>{fmt(m.totalSettled[active], currency)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: STONE }} />
          Included <span className="font-medium text-stone-700 dark:text-stone-200">{fmtUnits(m.includedUnits[active])}</span> · <span className={cn('text-stone-600 dark:text-stone-300', MONO)}>{fmt(volume.includedValueMonthly[active], currency)}</span>
          <InfoPopover term="includedRetailValue" />
        </span>
        <span className="inline-flex items-center gap-1.5 font-semibold text-stone-800 dark:text-stone-100">
          = Total <span>{fmtUnits(m.paidUnits[active] + m.includedUnits[active])}</span> · <span className={MONO}>{fmt(m.totalSettled[active] + volume.includedValueMonthly[active], currency)}</span>
        </span>
        <span className="ml-auto">{monthLabel(months[active])} · {m.includedShare[active]}% included</span>
      </div>

      {/* Total production value — economic value produced, however funded (period) */}
      <div className="mt-5 rounded-xl border border-stone-200 bg-stone-50/60 p-4 dark:border-stone-800 dark:bg-stone-800/30">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <span className="inline-flex flex-wrap items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-stone-500 dark:text-stone-400">
            Total production value
            <InfoPopover term="productionValue" />
            <span className="ml-1 normal-case tracking-normal text-stone-400 dark:text-stone-500">· {periodLabel}</span>
          </span>
          <div className="text-right">
            <p className={cn('text-2xl font-semibold tracking-tight', MONO)}>{fmt(production, currency)}</p>
            <MetricDeltas current={production} prevMonth={comparison.prevMonth} prevYear={comparison.prevYear} prevYearLabel={comparison.prevYearLabel} prevLabel={comparison.prevLabel} currency={currency} />
          </div>
        </div>
        <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full bg-stone-200/60 dark:bg-stone-700/40">
          <div style={{ width: `${pctOf(newCash)}%`, backgroundColor: SLATE }} />
          <div style={{ width: `${pctOf(pcCredit)}%`, backgroundColor: CHAMP }} />
          <div style={{ width: `${pctOf(includedRetail)}%`, backgroundColor: STONE }} />
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[11px]">
          <ValFig dot={SLATE} label="New cash (card & inv)" value={fmt(newCash, currency)} info="paidCardInvoice" />
          <ValFig dot={CHAMP} label="PC credit (pre-paid)" value={fmt(pcCredit, currency)} info="paidPcCredit" />
          <ValFig dot={STONE} label="Included (retail value)" value={fmt(includedRetail, currency)} info="includedRetailValue" />
        </div>
      </div>

      {/* Production value by category — Planning Center (Discipline | Stage), Lab, Unclassified */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Production value by category
          <InfoPopover term="category" />
        </span>
        {groups && (
          <span className="inline-flex items-center gap-1">
            <SegmentedToggle options={PC_VIEWS} value={view} onChange={onViewChange} size="sm" ariaLabel="Planning Center volume by discipline or stage" />
            <InfoPopover term="stage" />
          </span>
        )}
      </div>
      {groups ? (
        <>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="volume-by-category">
              <thead>
                <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-700 dark:text-stone-500">
                  <th className="pb-2.5 pr-3 text-left font-medium">{view === 'Stage' ? 'Department › Stage / Category' : 'Department › Category'}</th>
                  <th className="pb-2.5 px-3 text-right font-medium">Paid u.</th>
                  <th className="pb-2.5 px-3 text-right font-medium">Incl. u.</th>
                  <th className="pb-2.5 px-3 text-right font-medium">Incl. %</th>
                  <th className="pb-2.5 px-3 text-right font-medium"><span className="inline-flex items-center gap-1">New cash <InfoPopover term="paidCardInvoice" /></span></th>
                  <th className="pb-2.5 px-3 text-right font-medium"><span className="inline-flex items-center gap-1">PC credit <InfoPopover term="paidPcCredit" /></span></th>
                  <th className="pb-2.5 px-3 text-right font-semibold text-stone-600 dark:text-stone-300"><span className="inline-flex items-center gap-1">Gross value <InfoPopover term="grossSale" /></span></th>
                  <th className="pb-2.5 pl-3 text-right font-medium"><span className="inline-flex items-center gap-1">Included value (est.) <InfoPopover term="includedValueEst" /></span></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) =>
                  g.line ? (
                    <VolumeLine key={g.id} dataLine={g.line.id} name={<LineName ref_={g.line} />} f={g.subtotal} incl={deptIncl(g)} currency={currency} />
                  ) : (
                    [
                      <VolumeLine
                        key={`dept:${g.id}`}
                        dataDepartment={g.id}
                        strong
                        name={
                          <span className="inline-flex items-center gap-1">
                            {g.name}
                            <span className="text-[11px] font-normal text-stone-400 dark:text-stone-500">{childHint(g, view)}</span>
                            {g.info && <InfoPopover term={g.info} />}
                          </span>
                        }
                        f={g.subtotal}
                        incl={deptIncl(g)}
                        currency={currency}
                      />,
                      ...g.children.map((c) => (
                        <VolumeLine key={`${c.ref.kind}:${c.ref.id}`} dataLine={c.ref.id} indent name={<LineName ref_={c.ref} />} f={c.value} incl={childIncl(g, c.value)} currency={currency} />
                      )),
                    ]
                  ),
                )}
                <tr className="border-t-2 border-stone-300 dark:border-stone-600">
                  <td className="pt-3 pr-3 font-semibold">Total</td>
                  <td className={cn('pt-3 px-3 text-right font-bold', MONO)}>{fmtUnits(tot.paidUnits)}</td>
                  <td className={cn('pt-3 px-3 text-right font-bold', MONO)}>{fmtUnits(tot.includedUnits)}</td>
                  <td className={cn('pt-3 px-3 text-right font-bold', MONO)}>{totUnits ? Math.round((tot.includedUnits / totUnits) * 100) : 0}%</td>
                  <td className={cn('pt-3 px-3 text-right font-bold', MONO)}>{fmt(tot.paidCardInv, currency)}</td>
                  <td className={cn('pt-3 px-3 text-right font-bold text-champagne-700 dark:text-champagne-300', MONO)}>{fmt(tot.paidPcCredit, currency)}</td>
                  <td className={cn('pt-3 px-3 text-right font-bold', MONO)}>{fmt(tot.paidValue, currency)}</td>
                  <td className={cn('pt-3 pl-3 text-right font-bold italic text-stone-500 dark:text-stone-400', MONO)}>{fmt(includedRetail, currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-stone-400 dark:text-stone-500">
            Gross value is the paid (chargeable) work — new cash + PC credit. Planning Center + Lab + Unclassified sum to the total; the Stage view is a second cut of the same Planning Center subtotal. Included value (est.) prices each category&rsquo;s free units at its average paid rate — actual included pricing isn&rsquo;t recorded by category, so the lines are allocated to sum to the included retail total.
          </p>
        </>
      ) : (
        <p className="mt-3 text-sm text-stone-500 dark:text-stone-400">
          This data bundle predates the Business unit › Department › Category view. Upload a refresh built with the current export (schema v2) to see production value by category.
        </p>
      )}
    </div>
  )
}

/** A line's name with its glossary popover (category, stage or the Unclassified remainder). */
function LineName({ ref_ }: { ref_: LineRef }) {
  return (
    <span className="inline-flex items-center gap-1">
      {ref_.name}
      <InfoPopover term={lineTerm(ref_)} />
    </span>
  )
}

/** One row of the volume table — a department subtotal (strong), a category or
 *  stage under it (indent), or the single Unclassified line. */
function VolumeLine({
  name,
  f,
  incl,
  currency,
  strong,
  indent,
  dataLine,
  dataDepartment,
}: {
  name: ReactNode
  f: VolumeFig
  incl: number
  currency: string
  strong?: boolean
  indent?: boolean
  dataLine?: string
  dataDepartment?: string
}) {
  const units = f.paidUnits + f.includedUnits
  const num = cn('py-2.5 px-3 text-right', strong ? 'font-semibold text-stone-700 dark:text-stone-200' : 'text-stone-500 dark:text-stone-400', MONO)
  return (
    <tr data-line={dataLine} data-department={dataDepartment} className={cn('border-b border-stone-50 dark:border-stone-800/60', strong && 'bg-stone-50/70 dark:bg-stone-800/40')}>
      <td className={cn('py-2.5 pr-3', strong ? 'pl-2 font-semibold text-stone-800 dark:text-stone-100' : indent ? 'text-stone-700 dark:text-stone-200' : 'pl-2 font-medium text-stone-800 dark:text-stone-100')}>
        {indent ? (
          <span className="flex items-center gap-1.5 pl-5">
            <span className="h-1 w-1 shrink-0 rounded-full bg-stone-300 dark:bg-stone-600" aria-hidden />
            {name}
          </span>
        ) : (
          name
        )}
      </td>
      <td className={num}>{fmtUnits(f.paidUnits)}</td>
      <td className={num}>{fmtUnits(f.includedUnits)}</td>
      <td className={num}>{units ? Math.round((f.includedUnits / units) * 100) : 0}%</td>
      <td className={cn('py-2.5 px-3 text-right', strong && 'font-semibold', MONO)}>{fmt(f.paidCardInv, currency)}</td>
      <td className={cn('py-2.5 px-3 text-right text-champagne-700 dark:text-champagne-300', strong && 'font-semibold', MONO)}>{f.paidPcCredit ? fmt(f.paidPcCredit, currency) : '—'}</td>
      <td className={cn('py-2.5 px-3 text-right font-semibold', MONO)}>{fmt(f.paidValue, currency)}</td>
      <td className={cn('py-2.5 pl-3 text-right italic text-stone-400 dark:text-stone-500', MONO)}>{f.includedUnits ? fmt(incl, currency) : '—'}</td>
    </tr>
  )
}

function ValFig({ dot, label, value, info }: { dot: string; label: string; value: string; info?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: dot }} />
      <span className="text-stone-500 dark:text-stone-400">{label}</span>
      <span className={cn('font-medium text-stone-700 dark:text-stone-200', MONO)}>{value}</span>
      {info && <InfoPopover term={info} />}
    </span>
  )
}
